// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Gift, Plus } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useRewardCampaigns, type RewardCampaign, type RewardCampaignFilters } from '@/hooks/useRewardCampaigns'
import { formatBaht } from '@/lib/money'

export function CampaignsListPage() {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<RewardCampaignFilters>({})
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useRewardCampaigns(page, filters)
  const campaigns = data?.data ?? []

  const statusOptions = [
    { value: '', label: tc('labels.all') },
    { value: 'draft', label: t('campaigns.status.draft') },
    { value: 'active', label: t('campaigns.status.active') },
    { value: 'paused', label: t('campaigns.status.paused') },
    { value: 'closed', label: t('campaigns.status.closed') },
  ]

  const statusVariants: Record<string, 'success' | 'warning' | 'ghost' | 'primary'> = {
    active: 'success',
    draft: 'ghost',
    paused: 'warning',
    closed: 'ghost',
  }

  const columns: Column<RewardCampaign>[] = [
    {
      key: 'name',
      header: t('campaigns.fields.name'),
      render: (c) => (
        <div>
          <span className="font-medium">{c.name}</span>
          {c.description && (
            <p className="text-sm text-base-content/60 truncate max-w-xs">{c.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: tc('labels.status'),
      render: (c) => (
        <Badge variant={statusVariants[c.status] ?? 'ghost'}>
          {t(`campaigns.status.${c.status}`)}
        </Badge>
      ),
    },
    {
      key: 'earn_model',
      header: t('campaigns.fields.earn_model'),
      render: (c) => t(`campaigns.earn_model.${c.earn_model}`),
    },
    {
      key: 'pool_remaining',
      header: t('campaigns.fields.pool_remaining'),
      render: (c) => (
        <span>
          {formatBaht(c.pool_remaining)}
          <span className="text-base-content/40"> / {formatBaht(c.pool_total)}</span>
        </span>
      ),
      hideBelow: 'md',
    },
    {
      key: 'partner_shops_count',
      header: t('campaigns.fields.partner_shops_count'),
      render: (c) => c.partner_shops_count,
      hideBelow: 'lg',
    },
    {
      key: 'catalog_items_count',
      header: t('campaigns.fields.catalog_items_count'),
      render: (c) => c.catalog_items_count,
      hideBelow: 'lg',
    },
  ]

  const activeFilterCount = [filters.status || ''].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('campaigns.title') }]} />

      <PageHeader
        title={t('campaigns.title')}
        description={t('campaigns.subtitle')}
        isRefreshing={isFetching && !isLoading}
        actions={
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/incentives/campaigns/new')}>
            {t('campaigns.new_button')}
          </Button>
        }
      />

      <Card>
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium"
          aria-expanded={mobileFiltersOpen}
          aria-controls="campaigns-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="campaigns-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          <Select
            label={tc('labels.status')}
            value={filters.status || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, status: e.target.value || undefined }))
              setPage(1)
            }}
            options={statusOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={tc('errors.something_went_wrong')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !campaigns.length ? (
        <Card>
          <EmptyState
            icon={<Gift className="w-12 h-12" />}
            title={t('campaigns.empty.title')}
            description={t('campaigns.empty.description')}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/incentives/campaigns/new')}>
                {t('campaigns.new_button')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={campaigns}
            keyExtractor={(c) => c.id}
            onRowClick={(c) => navigate(`/incentives/campaigns/${c.id}`)}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
